import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import StatusChip, { subscriptionStatusMap } from '../common/feedback/StatusChip';
import { Subscription, Team } from '../../types';
import { formatDate } from '../../utils/date-formatter';

interface SubscriptionDetailsPanelProps {
  subscription: Subscription;
  teams: Team[];
}

const SubscriptionDetailsPanel: React.FC<SubscriptionDetailsPanelProps> = ({
  subscription,
  teams
}) => {
  // Get teams assigned to this subscription
  const assignedTeams = teams.filter(team =>
    team.subscriptionId === (subscription.id || subscription._id)
  );

  // Format bytes to human-readable
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Parse add-ons from Stripe subscription items array
  const parseAddOns = () => {
    if (!subscription.addOns || !Array.isArray(subscription.addOns)) {
      return { extraTeams: 0, extraUsers: 0, extraStorage: 0, extraApiCalls: 0 };
    }

    const addOns = { extraTeams: 0, extraUsers: 0, extraStorage: 0, extraApiCalls: 0 };

    subscription.addOns.forEach((item: any) => {
      const quantity = item.quantity || 0;
      const packSize = item.pack_size || 1;
      const totalValue = quantity * packSize;

      switch (item.type) {
        case 'teams':
          addOns.extraTeams += totalValue;
          break;
        case 'concurrent_users':
          addOns.extraUsers += totalValue;
          break;
        case 'storage':
          // Storage pack_size is in MB, convert to bytes
          addOns.extraStorage += totalValue * 1024 * 1024;
          break;
        case 'api_calls':
          addOns.extraApiCalls += totalValue;
          break;
      }
    });

    return addOns;
  };

  const parsedAddOns = parseAddOns();

  // Calculate total resources (limits already include add-ons from backend)
  const maxTeams = subscription.limits?.maxTeams ?? subscription.limits?.teamsPerOrg ?? 0;
  const maxStorage = subscription.limits?.maxStorageBytes ?? subscription.limits?.storage ?? 0;
  const maxUsers = subscription.limits?.maxConcurrentUsers ?? subscription.limits?.usersPerOrg ?? 0;

  // Calculate base plan values by subtracting add-ons from totals
  const baseTeams = maxTeams - parsedAddOns.extraTeams;
  const baseStorage = maxStorage - parsedAddOns.extraStorage;
  const baseUsers = maxUsers - parsedAddOns.extraUsers;

  // Check if any add-ons exist
  const hasAddOns = subscription.addOns && Array.isArray(subscription.addOns) && subscription.addOns.length > 0;

  // Debug logging (optional - can be removed in production)
  if (process.env.NODE_ENV === 'development') {
    console.log('SubscriptionDetailsPanel:', {
      name: subscription.name,
      hasAddOns,
      addOnsCount: Array.isArray(subscription.addOns) ? subscription.addOns.length : 0
    });
  }

  return (
    <Paper sx={{ p: 3, mt: 2, mb: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          {subscription.name || 'Subscription Details'}
        </Typography>
        <StatusChip
          status={subscription.status}
          statusMap={subscriptionStatusMap}
          size="medium"
        />
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Subscription Details Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Left Column - Basic Info */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Plan & Billing
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Base Plan Type</Typography>
            <Typography variant="body2" fontWeight="medium">{subscription.plan}</Typography>
          </Box>

          {subscription.referenceName && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Reference Name</Typography>
              <Typography variant="body2" fontWeight="medium">{subscription.referenceName}</Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Current Period Start</Typography>
            <Typography variant="body2">{formatDate(subscription.currentPeriodStart || subscription.startDate)}</Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Current Period End</Typography>
            <Typography variant="body2">
              {subscription.status === 'cancelled' ? 'Canceled' : formatDate(subscription.currentPeriodEnd || subscription.endDate)}
            </Typography>
          </Box>

          {subscription.trialEndDate && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Trial Ends</Typography>
              <Typography variant="body2">{formatDate(subscription.trialEndDate)}</Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Auto Renew</Typography>
            <Typography variant="body2">
              {subscription.status === 'cancelled'
                ? 'Cancelled'
                : (subscription.autoRenew !== false ? 'Yes' : 'No')}
            </Typography>
          </Box>
        </Grid>

        {/* Right Column - Resource Limits */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Resource Limits
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Teams</Typography>
            <Typography variant="body2" fontWeight="medium">{maxTeams}</Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Concurrent Users</Typography>
            <Typography variant="body2" fontWeight="medium">{maxUsers}</Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Storage</Typography>
            <Typography variant="body2" fontWeight="medium">{formatBytes(maxStorage)}</Typography>
          </Box>

          {subscription.limits?.apiCalls && subscription.limits.apiCalls > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">API Calls</Typography>
              <Typography variant="body2" fontWeight="medium">{subscription.limits.apiCalls}</Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Add-ons Breakdown Table - only show if add-ons exist */}
      {hasAddOns && (
        <>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Resource Breakdown
          </Typography>
          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Source</TableCell>
                  <TableCell align="right">Teams</TableCell>
                  <TableCell align="right">Users</TableCell>
                  <TableCell align="right">Storage</TableCell>
                  {parsedAddOns.extraApiCalls > 0 && <TableCell align="right">API Calls</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Base Plan Row */}
                <TableRow>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      Base Plan ({subscription.plan})
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{baseTeams}</TableCell>
                  <TableCell align="right">{baseUsers}</TableCell>
                  <TableCell align="right">{formatBytes(baseStorage)}</TableCell>
                  {parsedAddOns.extraApiCalls > 0 && (
                    <TableCell align="right">{(subscription.limits?.apiCalls ?? 0) - parsedAddOns.extraApiCalls}</TableCell>
                  )}
                </TableRow>

                {/* Add-on Rows */}
                {subscription.addOns && Array.isArray(subscription.addOns) && subscription.addOns.map((addOn: any, index: number) => {
                  const quantity = addOn.quantity || 0;
                  const packSize = addOn.pack_size || 1;
                  const totalValue = quantity * packSize;

                  return (
                    <TableRow key={addOn.id || index}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label="Add-on"
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          <Typography variant="body2" color="text.secondary">
                            {quantity} × {packSize} {addOn.type?.replace('_', ' ')}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {addOn.type === 'teams' ? totalValue : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {addOn.type === 'concurrent_users' ? totalValue : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {addOn.type === 'storage' ? formatBytes(totalValue * 1024 * 1024) : '-'}
                      </TableCell>
                      {parsedAddOns.extraApiCalls > 0 && (
                        <TableCell align="right">
                          {addOn.type === 'api_calls' ? totalValue : '-'}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}

                {/* Total Row */}
                <TableRow sx={{ '& td': { borderTop: 2, borderColor: 'divider', fontWeight: 'bold' } }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      Total Available
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{maxTeams}</TableCell>
                  <TableCell align="right">{maxUsers}</TableCell>
                  <TableCell align="right">{formatBytes(maxStorage)}</TableCell>
                  {parsedAddOns.extraApiCalls > 0 && (
                    <TableCell align="right">{subscription.limits?.apiCalls ?? 0}</TableCell>
                  )}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ mb: 3 }} />
        </>
      )}

      {/* Teams Table */}
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Assigned Teams ({assignedTeams.length})
      </Typography>

      {assignedTeams.length === 0 ? (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No teams assigned to this subscription
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Team Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Members</TableCell>
                <TableCell align="right">Storage Used</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignedTeams.map((team) => (
                <TableRow key={team.id || team._id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {team.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {team.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {team.membersCount || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatBytes(team.storageUsedBytes || 0)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default SubscriptionDetailsPanel;
